import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { SepaGuard } from '@/components/guards/SepaGuard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useFolderDetail, useFolderEstablishments } from '@/hooks/useDomains';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  BarChart3,
  Calendar,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Clock,
  Download,
  FileEdit,
  FileText,
  FolderOpen,
  Lock,
  Mail,
  Phone,
  Receipt,
  Server,
  Settings,
  Shield,
  Unlock,
  User,
  UserPlus,
  Users as UsersIcon,
  Trash2,
  XCircle,
  Building2,
  Zap,
  Send,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useArchiveFolder,
  useFolderAccess,
  useGrantFolderAccess,
  useOrganisationMembers,
  useRestrictFolder,
  useRevokeFolderAccess,
} from '@/hooks/useOrganisations';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useCurrentMemberRole } from '@/hooks/useCurrentMemberRole';
import { AutomationDrawer } from '../components/AutomationDrawer';
import { OngletVariables } from '@/features/demande-variables/components/OngletVariables';
import { useEnvoisDocuments } from '@/hooks/useEnvoisDocuments';
import { AutomationHistory } from '../components/AutomationHistory';
import { BulletinsDrawer } from '../components/BulletinsDrawer';
import { DownloadBulletinsDrawer } from '../components/DownloadBulletinsDrawer';
import { DsnDrawer } from '../components/DsnDrawer';
import { EditionsDrawer } from '../components/EditionsDrawer';
import { EstablishmentsList } from '../components/EstablishmentsList';
import { SenderConfigDisplay } from '../components/SenderConfigDisplay';
import { SenderConfigModal } from '../components/SenderConfigModal';
import { TemplateConfigSection } from '../components/TemplateConfigSection';
import { DocumentsView } from '@/features/ged';
import { EmployeesList, employeesKeys } from '@/features/employees';
import { domainsService } from '@/services/domains.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCcw, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/app';
import { toast } from 'sonner';
import { DashboardDrawer } from '@/features/dashboard-rh';
import { useFolderVault } from '@/hooks/useVault';
import { VaultDrawer } from '@/features/vault/components';
import { AccesEntreprisePanel } from '@/features/invitations/components/AccesEntreprisePanel';
import { ModulesEtPremierAccesPanel } from '@/features/invitations/components/ModulesEtPremierAccesPanel';

export function FolderDetailPage() {
  const { t } = useTranslation();
  const { folderUuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const autoSyncAttemptedFolderRef = useRef<string | null>(null);
  const [isAutomationDrawerOpen, setIsAutomationDrawerOpen] = useState(false);
  const [isEditionsDrawerOpen, setIsEditionsDrawerOpen] = useState(false);
  const [isDsnDrawerOpen, setIsDsnDrawerOpen] = useState(false);
  const [isBulletinsDrawerOpen, setIsBulletinsDrawerOpen] = useState(false);
  const [isDownloadBulletinsDrawerOpen, setIsDownloadBulletinsDrawerOpen] =
    useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSenderConfigModalOpen, setIsSenderConfigModalOpen] = useState(false);
  const [isDashboardDrawerOpen, setIsDashboardDrawerOpen] = useState(false);
  const [isVaultDrawerOpen, setIsVaultDrawerOpen] = useState(false);
  const [areActionsOpen, setAreActionsOpen] = useState(false);

  const { mutate: archiveFolder, isPending: isArchiving } = useArchiveFolder();
  const { mutate: restrictFolder, isPending: isRestricting } =
    useRestrictFolder();
  const { currentOrganisation } = useOrganisationsStore();
  const { isAdmin, role: roleDansLeCabinet } = useCurrentMemberRole();
  const [parametres] = useSearchParams();
  const ongletDemande = parametres.get('onglet');
  // `?colonnes=1` (depuis Paramètres → masque de saisie) : l'onglet Variables
  // s'ouvre avec le tiroir des colonnes.
  const colonnesDemandees = parametres.get('colonnes') === '1';
  // Même règle que l'API (`peutValider`) : un lecteur voit, il n'envoie pas.
  // Sans organisation (compte personnel), on agit sur ses propres dossiers.
  const peutAgirSurLaCollecte =
    !currentOrganisation || roleDansLeCabinet !== 'viewer';
  const { data: folderAccessList, isLoading: isLoadingAccess } =
    useFolderAccess(folderUuid || '');
  const { data: orgMembers } = useOrganisationMembers(
    currentOrganisation?.uuid || ''
  );
  const { mutate: grantAccess, isPending: isGranting } = useGrantFolderAccess();
  const { mutate: revokeAccess, isPending: isRevoking } =
    useRevokeFolderAccess();

  const {
    data: folderDetail,
    isLoading: isLoadingFolder,
    error: folderError,
    refetch: refetchFolderDetail,
  } = useFolderDetail(folderUuid);

  const { data: folderVault, isLoading: isLoadingVault } = useFolderVault(
    folderUuid || '',
    !!folderUuid
  );

  const {
    data: establishmentsData,
    isLoading: isLoadingEstablishments,
    error: establishmentsError,
  } = useFolderEstablishments(folderUuid);

  // Ce qui est réellement parti par courriel pour ce dossier (01/09/2026).
  const {
    data: envois = [],
    isError: envoisIndisponibles,
    refetch: rechargerEnvois,
  } = useEnvoisDocuments(folderUuid);

  // Folder sync mutation
  const syncFolderMutation = useMutation({
    mutationFn: async () => {
      if (!folderDetail?.data?.domain?.uuid || !folderUuid) {
        throw new Error('Missing domain or folder UUID');
      }
      return domainsService.syncFolderData(
        folderDetail.data.domain.uuid,
        folderUuid
      );
    },
    onSuccess: response => {
      toast.success(
        response.message ||
          t('folders.sync.success', 'Salariés et contrats mis à jour')
      );
      queryClient.invalidateQueries({
        queryKey: ['folder', 'establishments', folderUuid],
      });
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      refetchFolderDetail();
    },
    onError: () => {
      toast.error(t('folders.sync.error', 'Erreur lors de la synchronisation'));
    },
  });

  // Auto-sync on folder open if not synced
  useEffect(() => {
    const folder = folderDetail?.data;
    const domainUuid = folder?.domain?.uuid;
    if (
      folder &&
      !folder.isSynchro &&
      domainUuid &&
      folderUuid &&
      !isSyncing &&
      autoSyncAttemptedFolderRef.current !== folderUuid
    ) {
      // Une seule tentative automatique par ouverture de dossier. En cas de
      // panne OpenPaye, reboucler à chaque rendu multiplierait les appels sans
      // donner davantage d'information à l'utilisateur.
      autoSyncAttemptedFolderRef.current = folderUuid;
      setIsSyncing(true);
      const autoSync = async () => {
        try {
          const response = await domainsService.syncFolderData(
            domainUuid,
            folderUuid
          );
          toast.success(
            response.message ||
              t(
                'folders.sync.autoSyncComplete',
                'Salariés et contrats mis à jour'
              )
          );
          queryClient.invalidateQueries({
            queryKey: ['folder', 'establishments', folderUuid],
          });
          queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
          refetchFolderDetail();
        } catch (error) {
          console.error('Auto-sync failed:', error);
          toast.error(
            t(
              'folders.sync.error',
              'Impossible de mettre à jour les salariés et contrats. Les anciennes données sont conservées.'
            )
          );
        } finally {
          setIsSyncing(false);
        }
      };
      autoSync();
    }
  }, [
    folderDetail?.data,
    folderUuid,
    isSyncing,
    queryClient,
    refetchFolderDetail,
    t,
  ]);

  const isLoading = isLoadingFolder;
  const error = folderError;

  if (isLoading) {
    return (
      <div className="mx-auto space-y-6 p-6">
        <LoadingSkeleton className="h-8 w-64" />
        <LoadingSkeleton className="h-12 w-full" />
        <LoadingSkeleton className="h-96" />
      </div>
    );
  }

  if (error || !folderDetail) {
    return (
      <div className="mx-auto space-y-6 p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              {t('folders.detail.title', 'Détail du dossier')}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-error-100 mb-4 rounded-full p-3">
            <XCircle className="text-error-600 h-6 w-6" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('common.error', 'Erreur')}
          </h3>
          <p className="text-sm text-neutral-600">
            {t(
              'folders.detail.error',
              'Impossible de charger les détails du dossier'
            )}
          </p>
        </div>
      </div>
    );
  }

  const { data: folder } = folderDetail;
  const establishments = establishmentsData?.data || [];
  const hasText = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;
  const totalDocuments = folder.metadata?.totalDocuments;
  const hasDocumentCount =
    typeof totalDocuments === 'number' && Number.isFinite(totalDocuments);
  const lastModifiedDate = folder.metadata?.lastModified
    ? new Date(folder.metadata.lastModified)
    : null;
  const lastModifiedLabel =
    lastModifiedDate && !Number.isNaN(lastModifiedDate.getTime())
      ? lastModifiedDate.toLocaleDateString('fr-FR')
      : null;
  const hasContactInformation = [
    folder.nomContact,
    folder.adresseEmail,
    folder.telephone,
    folder.responsableDossier,
  ].some(hasText);

  const getStatusIcon = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return <XCircle className="text-error-500 h-4 w-4" />;
    if (!isSynchro) return <Clock className="text-warning-500 h-4 w-4" />;
    return <CheckCircle className="text-success-500 h-4 w-4" />;
  };

  const getStatusText = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return t('common.inactive', 'Inactif');
    if (!isSynchro) return t('common.notSynced', 'Non synchronisé');
    return t('common.active', 'Actif');
  };

  const getStatusColor = (isActive: boolean, isSynchro: boolean) => {
    if (!isActive) return 'text-error-600 bg-error-50';
    if (!isSynchro) return 'text-warning-600 bg-warning-50';
    return 'text-success-600 bg-success-50';
  };

  const handleAutomationClick = (_folder: unknown) => {
    setIsAutomationDrawerOpen(true);
  };

  const handleDataRefresh = () => {
    refetchFolderDetail();
  };

  return (
    <div className="mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-start gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t('common.back', 'Retour')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="min-w-0 flex-1 space-y-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              {folder.name}
            </h1>
            <p className="text-sm text-neutral-600">
              {t(
                'folders.detail.description',
                'Informations détaillées et actions sur le dossier'
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          {getStatusIcon(folder.isActive, folder.isSynchro)}
          <span
            className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(
              folder.isActive,
              folder.isSynchro
            )}`}
          >
            {getStatusText(folder.isActive, folder.isSynchro)}
          </span>

          {/* Archive/Restrict badges */}
          {folder.isArchived && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700">
              <Archive className="h-3.5 w-3.5" />
              {t('folders.status.archived', 'Archivé')}
            </span>
          )}
          {folder.isRestricted && (
            <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-700">
              <Lock className="h-3.5 w-3.5" />
              {t('folders.status.restricted', 'Restreint')}
            </span>
          )}

          {/* L'assistant, avec ce dossier déjà en contexte : on n'a pas à le
              nommer dans chaque demande. */}
          <Link
            to={`${ROUTES.ASSISTANT}?dossier=${folder.uuid}`}
            className="bg-brand-50 text-brand-700 hover:bg-brand-100 flex min-h-9 items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            Assistant
          </Link>

          {/* Sync Button */}
          <button
            onClick={() => syncFolderMutation.mutate()}
            disabled={syncFolderMutation.isPending || isSyncing}
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={t(
              'folders.actions.sync',
              'Mettre à jour salariés et contrats'
            )}
          >
            <RefreshCcw
              className={`h-4 w-4 ${syncFolderMutation.isPending || isSyncing ? 'animate-spin' : ''}`}
            />
            <span>
              {syncFolderMutation.isPending || isSyncing
                ? t('folders.sync.syncing', 'Mise à jour...')
                : t(
                    'folders.actions.sync',
                    'Mettre à jour salariés et contrats'
                  )}
            </span>
          </button>

          {/* Archive Button - admin/owner only */}
          {isAdmin && (
            <button
              onClick={() =>
                archiveFolder({
                  folderUuid: folderUuid!,
                  archive: !folder.isArchived,
                })
              }
              disabled={isArchiving}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                folder.isArchived
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
            >
              {folder.isArchived ? (
                <>
                  <ArchiveRestore className="h-4 w-4" />
                  {t('folders.actions.unarchive', 'Désarchiver')}
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" />
                  {t('folders.actions.archive', 'Archiver')}
                </>
              )}
            </button>
          )}

          {/* Restrict Button - admin/owner only */}
          {isAdmin && (
            <button
              onClick={() =>
                restrictFolder({
                  folderUuid: folderUuid!,
                  isRestricted: !folder.isRestricted,
                })
              }
              disabled={isRestricting}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                folder.isRestricted
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                  : 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              {folder.isRestricted ? (
                <>
                  <Unlock className="h-4 w-4" />
                  {t('folders.actions.unrestrict', "Libérer l'accès")}
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  {t('folders.actions.restrict', 'Restreindre')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Folder Access Management - visible when folder is restricted, admin/owner only */}
      {folder.isRestricted && isAdmin && (
        <div className="rounded-2xl border border-red-200 bg-white p-4 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100">
                <Lock className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t('folders.access.title', 'Gestion des accès')}
                </h3>
                <p className="text-sm text-neutral-600">
                  {t(
                    'folders.access.description',
                    'Ce dossier est restreint. Seuls les membres ci-dessous y ont accès.'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Members with access */}
          <div className="space-y-3">
            {isLoadingAccess ? (
              <div className="space-y-2">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse rounded-lg bg-neutral-100"
                  />
                ))}
              </div>
            ) : (
              <>
                {/* Current access list */}
                {folderAccessList && folderAccessList.length > 0 ? (
                  <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
                    {folderAccessList.map(access => (
                      <div
                        key={access.uuid}
                        className="flex items-center justify-between px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100">
                            <User className="h-4 w-4 text-neutral-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-900">
                              {access.member?.user?.firstName}{' '}
                              {access.member?.user?.lastName}
                            </p>
                            <p className="text-xs text-neutral-500">
                              {access.member?.user?.email}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            revokeAccess({
                              folderUuid: folderUuid!,
                              memberUuid: access.memberUuid,
                            })
                          }
                          disabled={isRevoking}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t('folders.access.revoke', 'Révoquer')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 py-6 text-center">
                    <UsersIcon className="mx-auto mb-2 h-6 w-6 text-neutral-400" />
                    <p className="text-sm text-neutral-500">
                      {t(
                        'folders.access.noMembers',
                        "Aucun membre n'a accès à ce dossier pour le moment."
                      )}
                    </p>
                  </div>
                )}

                {/* Add member dropdown */}
                {orgMembers &&
                  orgMembers.length > 0 &&
                  (() => {
                    const accessMemberUuids = new Set(
                      folderAccessList?.map(a => a.memberUuid) || []
                    );
                    const availableMembers = orgMembers.filter(
                      m => !accessMemberUuids.has(m.uuid) && m.role !== 'owner'
                    );

                    if (availableMembers.length === 0) return null;

                    return (
                      <div className="mt-4">
                        <p className="mb-2 text-sm font-medium text-neutral-700">
                          {t('folders.access.addMember', 'Ajouter un membre')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {availableMembers.map(member => (
                            <button
                              key={member.uuid}
                              onClick={() =>
                                grantAccess({
                                  folderUuid: folderUuid!,
                                  memberUuid: member.uuid,
                                })
                              }
                              disabled={isGranting}
                              className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:border-green-300 hover:bg-green-50 hover:text-green-700 disabled:opacity-50"
                            >
                              <UserPlus className="h-4 w-4" />
                              {member.user.firstName} {member.user.lastName}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
              </>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      {/* `?onglet=variables` : le mail « variables reçues » ouvre directement
          l'onglet à vérifier. */}
      <Tabs
        defaultValue={
          [
            'variables',
            'automations',
            'employees',
            'documents',
            'establishments',
            'acces',
          ].includes(ongletDemande ?? '') || colonnesDemandees
            ? colonnesDemandees
              ? 'variables'
              : (ongletDemande as string)
            : 'informations'
        }
        className="w-full"
      >
        <TabsList className="mb-6 w-full justify-start">
          <TabsTrigger value="informations" className="gap-2">
            <FolderOpen className="h-4 w-4" />
            {t('folders.tabs.informations', 'Informations')}
          </TabsTrigger>
          <TabsTrigger value="establishments" className="gap-2">
            <Building2 className="h-4 w-4" />
            {t('folders.tabs.establishments', 'Établissements')}
          </TabsTrigger>
          <TabsTrigger value="employees" className="gap-2">
            <UsersIcon className="h-4 w-4" />
            {t('folders.tabs.employees', 'Employés')}
          </TabsTrigger>
          <TabsTrigger value="automations" className="gap-2">
            <Zap className="h-4 w-4" />
            {t('folders.tabs.automations', 'Automatisations')}
          </TabsTrigger>
          <TabsTrigger value="documents" className="gap-2">
            <Archive className="h-4 w-4" />
            {t('folders.tabs.documents', 'Documents')}
          </TabsTrigger>
          {/* [C2] Les acteurs de l'ENTREPRISE — DRH, élus du CSE, auditeur.
              Un onglet de plus sur une fiche qui en a déjà cinq, et non une
              entrée de menu : la tâche s'applique à un objet déjà listé. */}
          <TabsTrigger value="acces" className="gap-2">
            <User className="h-4 w-4" />
            {t('folders.tabs.acces', 'Accès')}
          </TabsTrigger>
          {/* La demande de variables (21/09/2026) : ce que le client envoie
              chaque mois, le lien à lui envoyer, ses colonnes de saisie. Un
              territoire du dossier, pas une entrée de menu. */}
          <TabsTrigger value="variables" className="gap-2">
            <Send className="h-4 w-4" />
            Variables
          </TabsTrigger>
        </TabsList>

        <TabsContent value="variables">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <OngletVariables
              folderUuid={folder.uuid}
              nomDossier={folder.name}
              peutEcrireDirectement={peutAgirSurLaCollecte}
              ouvrirLesColonnes={colonnesDemandees}
            />
          </div>
        </TabsContent>

        {/* Tab 1: Informations */}
        <TabsContent value="informations" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main content */}
            <div className="space-y-6 lg:col-span-2">
              {/* Folder Information */}
              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="bg-primary-50 flex h-10 w-10 items-center justify-center rounded-lg">
                    <FolderOpen className="text-primary-600 h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t('folders.information', 'Informations du dossier')}
                    </h2>
                    <p className="text-sm text-neutral-500">
                      {t(
                        'folders.informationDescription',
                        'Détails et métadonnées'
                      )}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-900">
                        {t('folders.name', 'Nom du dossier')}
                      </label>
                      <div className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                        {folder.name}
                      </div>
                    </div>

                    {folder.domain && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-neutral-900">
                          {t('folders.domain', 'Domaine')}
                        </label>
                        <div className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                          <Server className="h-4 w-4" />
                          {folder.domain.name}
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-900">
                        {t('folders.remoteId', 'ID Distant')}
                      </label>
                      <div className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                        {folder.remoteId}
                      </div>
                    </div>

                    {folder.description && (
                      <div className="space-y-1 sm:col-span-2">
                        <label className="text-sm font-medium text-neutral-900">
                          {t('folders.description', 'Description')}
                        </label>
                        <div className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                          {folder.description}
                        </div>
                      </div>
                    )}

                    {hasDocumentCount && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-neutral-900">
                          {t('folders.documents', 'Documents')}
                        </label>
                        <div className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                          <FileText className="h-4 w-4" />
                          {totalDocuments}
                        </div>
                      </div>
                    )}

                    {lastModifiedLabel && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-neutral-900">
                          {t('folders.lastModified', 'Dernière modification')}
                        </label>
                        <div className="flex items-center gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                          <Calendar className="h-4 w-4" />
                          {lastModifiedLabel}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Une carte vide n'aide pas à agir : elle disparaît tant que
                  le dossier ne contient aucune coordonnée. */}
              {hasContactInformation && (
                <div className="rounded-lg border border-neutral-200 bg-white p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50">
                      <User className="h-5 w-5 text-green-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t('folders.contact', 'Contact')}
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {hasText(folder.nomContact) && (
                      <div className="flex items-center gap-3 text-sm">
                        <User className="h-4 w-4 text-neutral-400" />
                        <span className="font-medium text-neutral-900">
                          {folder.nomContact}
                        </span>
                      </div>
                    )}

                    {hasText(folder.adresseEmail) && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="h-4 w-4 text-neutral-400" />
                        <span className="text-neutral-600">
                          {folder.adresseEmail}
                        </span>
                      </div>
                    )}

                    {hasText(folder.telephone) && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-neutral-400" />
                        <span className="text-neutral-600">
                          {folder.telephone}
                        </span>
                      </div>
                    )}

                    {hasText(folder.responsableDossier) && (
                      <div className="flex items-center gap-3 text-sm">
                        <User className="h-4 w-4 text-neutral-400" />
                        <span className="text-neutral-700">
                          {t('folders.responsable', 'Manager')}:{' '}
                          {folder.responsableDossier}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Email Configuration */}
              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50">
                    <Mail className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t('emailConfiguration.title', 'Configuration Email')}
                    </h2>
                  </div>
                </div>
                <SenderConfigDisplay
                  senderConfigurations={folder.senderConfigurations}
                  onConfigure={() => setIsSenderConfigModalOpen(true)}
                />
              </div>

              {/* Template Configuration */}
              <TemplateConfigSection
                folderUuid={folderUuid!}
                currentTemplateConfigs={{}}
                onConfigUpdate={refetchFolderDetail}
              />
            </div>

            {/* Sidebar - Actions */}
            <div>
              <button
                type="button"
                aria-expanded={areActionsOpen}
                aria-controls="actions-du-dossier"
                onClick={() => setAreActionsOpen(open => !open)}
                className="focus-visible:ring-primary-600 flex min-h-11 w-full items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm font-semibold text-neutral-900 focus-visible:ring-2 focus-visible:outline-none"
              >
                {t('folders.actions.open', 'Actions du dossier')}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${areActionsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                id="actions-du-dossier"
                className={`${areActionsOpen ? 'block' : 'hidden'} space-y-6 pt-3`}
              >
                <div className="shadow-soft rounded-xl border border-neutral-200 bg-white p-6">
                  <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-50">
                      <Settings className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-md font-semibold text-neutral-900">
                        {t('folders.actions.title', 'Actions')}
                      </h3>
                      <p className="text-sm text-neutral-500">
                        {t(
                          'folders.actions.description',
                          'Gérez vos dossiers et automatisations'
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                    <SepaGuard>
                      <button
                        type="button"
                        onClick={() => setIsEditionsDrawerOpen(true)}
                        className="group hover:shadow-medium w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105 hover:border-purple-300 hover:bg-purple-50"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-purple-100 transition-colors group-hover:bg-purple-200">
                            <FileEdit className="h-8 w-8 text-purple-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-neutral-900">
                            {t('dashboard.folders.editions', 'Éditions')}
                          </h3>
                        </div>
                      </button>
                    </SepaGuard>

                    <SepaGuard>
                      <button
                        type="button"
                        onClick={() => setIsDsnDrawerOpen(true)}
                        className="group hover:shadow-medium w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105 hover:border-blue-300 hover:bg-blue-50"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-blue-100 transition-colors group-hover:bg-blue-200">
                            <ClipboardList className="h-8 w-8 text-blue-600" />
                          </div>
                          <h3 className="text-md font-semibold text-neutral-900">
                            {t('dashboard.folders.dsnEmail', 'DSN par mail')}
                          </h3>
                        </div>
                      </button>
                    </SepaGuard>

                    <SepaGuard>
                      <button
                        type="button"
                        onClick={() => setIsBulletinsDrawerOpen(true)}
                        className="group hover:shadow-medium w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105 hover:border-green-300 hover:bg-green-50"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-green-100 transition-colors group-hover:bg-green-200">
                            <Receipt className="h-8 w-8 text-green-600" />
                          </div>
                          <h3 className="text-md font-semibold text-neutral-900">
                            {t(
                              'dashboard.folders.bulletinsEmail',
                              'Bulletins par mail'
                            )}
                          </h3>
                        </div>
                      </button>
                    </SepaGuard>

                    <SepaGuard>
                      <button
                        type="button"
                        onClick={() => setIsDownloadBulletinsDrawerOpen(true)}
                        className="group hover:shadow-medium w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105 hover:border-emerald-300 hover:bg-emerald-50"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-emerald-100 transition-colors group-hover:bg-emerald-200">
                            <Download className="h-8 w-8 text-emerald-600" />
                          </div>
                          <h3 className="text-md font-semibold text-neutral-900">
                            {t(
                              'dashboard.folders.downloadBulletins',
                              'Télécharger les bulletins'
                            )}
                          </h3>
                        </div>
                      </button>
                    </SepaGuard>

                    <SepaGuard>
                      <button
                        type="button"
                        onClick={() => setIsDashboardDrawerOpen(true)}
                        className="group hover:shadow-medium w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105 hover:border-orange-300 hover:bg-orange-50"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-orange-100 transition-colors group-hover:bg-orange-200">
                            <BarChart3 className="h-8 w-8 text-orange-600" />
                          </div>
                          <h3 className="text-md font-semibold text-neutral-900">
                            {t(
                              'dashboard.folders.statistics',
                              'Tableaux de bord'
                            )}
                          </h3>
                        </div>
                      </button>
                    </SepaGuard>

                    {/* Unified Vault Card */}
                    <SepaGuard strict>
                      <button
                        type="button"
                        onClick={() => setIsVaultDrawerOpen(true)}
                        className="group hover:shadow-medium hover:border-primary-300 hover:bg-primary-50 w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-105"
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-primary-100 group-hover:bg-primary-200 mb-4 flex h-16 w-16 items-center justify-center rounded-xl transition-colors">
                            <Shield className="text-primary-600 h-8 w-8" />
                          </div>
                          <h3 className="text-md font-semibold text-neutral-900">
                            {t('vault.drawer.title', 'Coffre-Fort')}
                          </h3>
                          <p className="mt-1 text-xs text-neutral-500">
                            {isLoadingVault
                              ? t('vault.loading', 'Chargement...')
                              : folderVault
                                ? t('vault.drawer.configured', 'Coffre actif')
                                : t(
                                    'vault.drawer.notConfigured',
                                    'Cliquez pour activer'
                                  )}
                          </p>
                        </div>
                      </button>
                    </SepaGuard>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tab 2: Établissements */}
        <TabsContent value="establishments">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <EstablishmentsList
              establishments={establishments}
              isLoading={isLoadingEstablishments}
              error={establishmentsError}
            />
          </div>
        </TabsContent>

        {/* Tab 3: Employés */}
        <TabsContent value="employees">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <EmployeesList
              onRefresh={() => syncFolderMutation.mutate()}
              isRefreshing={syncFolderMutation.isPending || isSyncing}
            />
          </div>
        </TabsContent>

        {/* Tab 6: Accès de l'entreprise */}
        <TabsContent value="acces">
          {/* Ce que le cabinet OUVRE — visible de lui seul, le serveur le
              refuse à la direction de l'entreprise. */}
          <ModulesEtPremierAccesPanel folderUuid={folder?.uuid} />
          <AccesEntreprisePanel employerUuid={folder?.employerUuid} />
        </TabsContent>

        {/* Tab 4: Automatisations */}
        <TabsContent value="automations">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  {t('automation.title', 'Automatisation')}
                </h2>
                <p className="text-sm text-neutral-500">
                  {t('automation.description', 'Configuration et historique')}
                </p>
              </div>
            </div>

            <AutomationHistory
              configurations={folder.automationConfigurations}
              executions={folder.recentAutomationExecutions}
              folder={folder}
              onAutomationClick={handleAutomationClick}
              envois={envois}
              envoisIndisponibles={envoisIndisponibles}
              onRechargerEnvois={() => void rechargerEnvois()}
              configurationGuard={SepaGuard}
            />
          </div>
        </TabsContent>

        {/* Tab 5: Documents */}
        <TabsContent value="documents">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <DocumentsView
              context="folder"
              contextUuid={folderUuid || ''}
              contextName={folder.name}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Automation Drawer */}
      <AutomationDrawer
        isOpen={isAutomationDrawerOpen}
        onClose={() => setIsAutomationDrawerOpen(false)}
        folder={folder}
        onDataRefresh={handleDataRefresh}
      />

      {/* Sender Configuration Modal */}
      <SenderConfigModal
        isOpen={isSenderConfigModalOpen}
        onClose={() => setIsSenderConfigModalOpen(false)}
        folderUuid={folderUuid}
        currentConfig={folder.senderConfigurations?.[0]}
        onSave={() => {
          setIsSenderConfigModalOpen(false);
        }}
      />

      {/* Editions Drawer */}
      {folderUuid && (
        <EditionsDrawer
          isOpen={isEditionsDrawerOpen}
          onClose={() => setIsEditionsDrawerOpen(false)}
          folderUuid={folderUuid}
        />
      )}

      {/* DSN Drawer */}
      {folderUuid && (
        <DsnDrawer
          isOpen={isDsnDrawerOpen}
          onClose={() => setIsDsnDrawerOpen(false)}
          folderUuid={folderUuid}
          folderEmail={folder?.adresseEmail}
          onDataRefresh={handleDataRefresh}
        />
      )}

      {/* Bulletins Drawer */}
      {folderUuid && (
        <BulletinsDrawer
          isOpen={isBulletinsDrawerOpen}
          onClose={() => setIsBulletinsDrawerOpen(false)}
          folderUuid={folderUuid}
          folderEmail={folder?.adresseEmail}
          onDataRefresh={handleDataRefresh}
        />
      )}

      {/* Download Bulletins Drawer */}
      {folderUuid && (
        <DownloadBulletinsDrawer
          isOpen={isDownloadBulletinsDrawerOpen}
          onClose={() => setIsDownloadBulletinsDrawerOpen(false)}
          folderUuid={folderUuid}
          folderName={folder?.name}
        />
      )}

      {/* Dashboard Drawer */}
      {folderUuid && (
        <DashboardDrawer
          isOpen={isDashboardDrawerOpen}
          onClose={() => setIsDashboardDrawerOpen(false)}
          folderUuid={folderUuid}
          folderName={folder?.name || ''}
        />
      )}

      {/* Vault Drawer */}
      {folderUuid && (
        <VaultDrawer
          isOpen={isVaultDrawerOpen}
          onClose={() => setIsVaultDrawerOpen(false)}
          folderUuid={folderUuid}
          folderName={folder?.name || ''}
          folderEmail={folder?.adresseEmail}
          domainUuid={folder?.domain?.uuid}
          onDataRefresh={handleDataRefresh}
        />
      )}
    </div>
  );
}
